// Cookie Consent Utility
// Manages consent cookies for anonymous users to prevent repeated consent prompts

import { createLogger } from './logger';
import type { ConsentPreferences } from '../types/privacy';
import { isValidConsent, isPrivacyCompliant } from '../constants/privacy';

const logger = createLogger({ module: 'CookieConsent' });

// Cookie configuration
const CONSENT_COOKIE_NAME = 'careercard_consent';
const CONSENT_COOKIE_EXPIRY_DAYS = 365; // 1 year
const CONSENT_VERSION = '1.0';

interface ConsentCookie {
  consent: ConsentPreferences;
  version: string;
  timestamp: number;
  expires: number;
}

export class CookieConsentManager {
  private static instance: CookieConsentManager;

  private constructor() {}

  public static getInstance(): CookieConsentManager {
    if (!CookieConsentManager.instance) {
      CookieConsentManager.instance = new CookieConsentManager();
    }
    return CookieConsentManager.instance;
  }

  /**
   * Set consent cookie for anonymous users
   */
  setConsentCookie(consent: ConsentPreferences): boolean {
    try {
      if (!isValidConsent(consent) || !isPrivacyCompliant(consent)) {
        logger.warn('Invalid or non-compliant consent preferences provided for cookie');
        return false;
      }

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + CONSENT_COOKIE_EXPIRY_DAYS);

      const cookieData: ConsentCookie = {
        consent,
        version: CONSENT_VERSION,
        timestamp: Date.now(),
        expires: expiryDate.getTime()
      };

      const cookieValue = btoa(JSON.stringify(cookieData)); // Base64 encode for safety
      
      // Set cookie with secure flags
      const cookieString = [
        `${CONSENT_COOKIE_NAME}=${cookieValue}`,
        `expires=${expiryDate.toUTCString()}`,
        'path=/',
        'SameSite=Lax',
        // Only set Secure flag in production (HTTPS)
        ...(window.location.protocol === 'https:' ? ['Secure'] : [])
      ].join('; ');

      document.cookie = cookieString;
      
      logger.debug('Consent cookie set', { consent, expires: expiryDate });
      return true;
    } catch (error) {
      logger.error('Error setting consent cookie', error);
      return false;
    }
  }

  /**
   * Get consent from cookie
   */
  getConsentCookie(): ConsentPreferences | null {
    try {
      const cookies = document.cookie.split(';');
      const consentCookie = cookies.find(cookie => 
        cookie.trim().startsWith(`${CONSENT_COOKIE_NAME}=`)
      );

      if (!consentCookie) {
        return null;
      }

      const cookieValue = consentCookie.split('=')[1];
      if (!cookieValue) {
        return null;
      }

      // Decode and parse cookie data
      const cookieData: ConsentCookie = JSON.parse(atob(cookieValue));

      // Check if cookie is expired
      if (Date.now() > cookieData.expires) {
        this.clearConsentCookie();
        return null;
      }

      // Validate consent data
      if (!isValidConsent(cookieData.consent)) {
        this.clearConsentCookie();
        return null;
      }

      // Check version compatibility
      if (cookieData.version !== CONSENT_VERSION) {
        logger.info('Consent cookie version mismatch, clearing cookie');
        this.clearConsentCookie();
        return null;
      }

      return cookieData.consent;
    } catch (error) {
      logger.warn('Error reading consent cookie', error);
      this.clearConsentCookie(); // Clear corrupted cookie
      return null;
    }
  }

  /**
   * Check if user has previously given consent (cookie exists)
   */
  hasConsentCookie(): boolean {
    return this.getConsentCookie() !== null;
  }

  /**
   * Clear consent cookie
   */
  clearConsentCookie(): void {
    try {
      document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      logger.debug('Consent cookie cleared');
    } catch (error) {
      logger.warn('Error clearing consent cookie', error);
    }
  }

  /**
   * Update existing consent cookie with new preferences
   * This is useful when a user logs in and we need to sync the cookie with their database settings
   */
  updateConsentCookie(consent: ConsentPreferences): boolean {
    // Use the same validation and setting logic as setConsentCookie
    return this.setConsentCookie(consent);
  }

  /**
   * Check if specific consent type is granted via cookie
   */
  checkConsentFromCookie(consentType: keyof ConsentPreferences): boolean {
    const consent = this.getConsentCookie();
    if (!consent) {
      // No cookie = only necessary consent
      return consentType === 'necessary';
    }
    return consent[consentType] === true;
  }

  /**
   * Migrate localStorage consent to cookie (for existing users)
   */
  migrateFromLocalStorage(): boolean {
    try {
      const localStorageConsent = localStorage.getItem('careercard_anonymous_consent');
      if (!localStorageConsent) {
        return false;
      }

      const stored = JSON.parse(localStorageConsent);
      if (stored.consent && isValidConsent(stored.consent)) {
        const success = this.setConsentCookie(stored.consent);
        if (success) {
          // Clear localStorage after successful migration
          localStorage.removeItem('careercard_anonymous_consent');
          logger.info('Successfully migrated consent from localStorage to cookie');
          return true;
        }
      }
      return false;
    } catch (error) {
      logger.warn('Error migrating consent from localStorage', error);
      return false;
    }
  }
}

// Export singleton instance
export const cookieConsentManager = CookieConsentManager.getInstance(); 